import React from 'react';
import parse from 'html-react-parser';
import moment from 'moment';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import { Tooltip } from 'antd';

import { IAccount, IClientCash, ICondoGuests, ICondoUnit, ICondoUnits } from '@share/common-types';
import { DATE_FORMAT_UNIT } from '@constants';
import { getReserveUnitUrl, getSelectedCurrency, isCertificateApplied, isDisplayCertificates } from '@share/utils';
import { renderNumber, getNights, formatDateCheckInOut, formatDateCheckInOutWithLabel, creatingArrayWithNumber, UrlUtils } from '@share/utils';
import { BlueButton } from '@share/components';
import { Currency, ModalCancellationPolicies, WalletMessage, WalletRewardMessage } from '@components';
import { RootState } from '@share/utils';
import { IUnitsSearchState, setSelectedCondoReviewClientCash, setSelectedCondoReviewClientCashStr } from '@store/slices';
import { ICondoDetailsState, ICondosState, IMarginatorState, IMenuState, condosActions, marginatorActions } from '@share/store/slices';
import { ILoginState } from '@share/store/slices';
import { CondoMatchTypeEnum } from '@share/common-types';
import { CONDO_DATES_LABEL, CONDO_IS_FLEXIBLE_LABEL } from '@share/constants';
import { WalletPriceType, getWalletPrices } from '@utils';
import { RefundableEnum } from '@common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { InfoSvg, ManDarkSvg } from '@assets';

import { ModalUnitDetails } from '../../modal-unit-details';
import { ModalUnitDetailsNew } from '../../modal-unit-details-new';

import './style.scss';

interface IMapStateToProps {
  condosStore: ICondosState;
  condoGuestsStore: ICondoGuests;
  condoDetailsStore: ICondoDetailsState;

  unitsSearchStore: IUnitsSearchState;

  marginatorStore: IMarginatorState;

  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setSelectedCondoSearchClientCash: (selectedClientCahs: IClientCash) => void;
  setSelectedCondoReviewClientCash: (selectedClientCash: number) => void;
  setSelectedCondoReviewClientCashStr: (clientCash: string) => void;
  setMarginatorCommission: (commission: number) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  unit: ICondoUnit;
  condoId: number;
  isOnlyAlternative: boolean;
  isWalletSearch: boolean | undefined;
  isStaticDetail: boolean;
  certificateId?: string;
  certificateItemId?: string;
}

interface IState {
  isModalCancellationPoliciesVisible: boolean;
  isModalVisible: boolean;
  isStrict: boolean;
  endDate: string | null | undefined;
  startDate: string | null | undefined;
}

class CondoUnitRowComponent extends React.Component<IProps, IState> {
  state: IState = {
    isModalCancellationPoliciesVisible: false,
    isModalVisible: false,
    isStrict: false,
    startDate: null,
    endDate: null,
  };

  componentDidMount(): void {
    const values = UrlUtils.getValues();
    if (values[CONDO_IS_FLEXIBLE_LABEL] && values[CONDO_DATES_LABEL]) {
      const { endDate, startDate } = values[CONDO_DATES_LABEL] as { startDate: string; endDate: string };
      const isStrict: boolean = values[CONDO_IS_FLEXIBLE_LABEL] === '0' && !isEmpty(startDate) && !isEmpty(endDate);

      const startDateFormatted = startDate ? get(startDate.split('T'), '[0]') : null;
      const endDateFormatted = endDate ? get(endDate.split('T'), '[0]') : null;

      this.setState({ isStrict, endDate: endDateFormatted, startDate: startDateFormatted });
    }
  }

  changeMatchTypeForUnit = (unit: ICondoUnit, units: ICondoUnits[]) => {
    return units.map((item) => {
      return {
        ...item,
        availibilities: item.availibilities.map((availibility) => {
          if (
            unit.dateRange.from === availibility.dateRange.from &&
            unit.dateRange.to === availibility.dateRange.to
          ) {
            return { ...availibility, matchType: CondoMatchTypeEnum.Exact };
          }
          
          return availibility;
        }),
      };
    });
  };

  showCancellationPolicies = (): void => {
    this.setState({
      isModalCancellationPoliciesVisible: true,
    });
  };

  handleCancelModalCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: false });
  };

  handleCancel = (): void => {
    this.setState({ isModalVisible: !this.state.isModalVisible });
  };

  handleReviewCondo = async (marginatorCommission: number, accountName: string | null | undefined, condoId: number, unitId: string, walletPrices: any) => {
    const { isStaticDetail, loginStore, condosStore, history, certificateId, certificateItemId  } = this.props;
    const { selectedCondoSearchClientCash } = condosStore;
    const { account } = loginStore;

    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

    const certificateApplied = isCertificateApplied(account as IAccount, certificateId);

    if (!certificateApplied) {
      const selectedPropertyClientCash = displayWalletSavings ? walletPrices?.maxClientCashPermited : selectedCondoSearchClientCash?.selectedPropertyClientCash;

      this.props.setMarginatorCommission(marginatorCommission);
      await this.props.setSelectedCondoReviewClientCash(selectedPropertyClientCash);
      await this.props.setSelectedCondoReviewClientCashStr(selectedPropertyClientCash ? selectedPropertyClientCash.toString() : '');
      await this.props.setSelectedCondoSearchClientCash({ ...selectedCondoSearchClientCash, selectedPropertyReviewClientCash: selectedPropertyClientCash });
    }

    const reserveUnitUrl = getReserveUnitUrl(accountName, condoId, unitId, isStaticDetail);
    const displayCertificates = isDisplayCertificates(account as IAccount);
    const parsedCertificateId = certificateId?.trim();
    const parsedCertificateItemId = certificateItemId?.trim();

    history.push({
      pathname: reserveUnitUrl,
      state: parsedCertificateId && displayCertificates
        ? {
            certificateId: parsedCertificateId,
            ...(parsedCertificateItemId ? { certificateItemId: parsedCertificateItemId } : {}),
          }
        : undefined,
    });
  }

  render(): React.ReactNode {
    const { unit, condoId, isWalletSearch, loginStore, condoDetailsStore, condoGuestsStore, condosStore, marginatorStore, certificateId } = this.props;

    const { isModalCancellationPoliciesVisible, isModalVisible, isStrict, endDate, startDate } = this.state;
    const firstAvailability = unit.availibilities?.[0];
    const maxWalletClientCash = firstAvailability?.maxWalletClientCash;
    const maxRewardWalletAmount = firstAvailability?.maxRewardWalletAmount;
    const { dateRange, price, publicPrice, unitId, name, cancellationPolicyText, bathroomsCount, bedroomsCount, maxOccupancy, refundability, totalNights } = unit;
    const { account, userWalletData } = loginStore;
    const { condo } = condoDetailsStore;
    const { selectedCondoSearchClientCash } = condosStore;
    
    const value = marginatorStore.value || 0;

    const baseClassName = 'unit-row__left-block-text';
    const baseClassNameMobile = `${baseClassName}-mobile`;
    const baseClassNameDateOnlyAlternative = `${baseClassName} dates}`;
    const baseClassNameOnlyAlternative = `${baseClassName}`;
    const baseClassNameOnlyAlternativeMobile = `${baseClassNameMobile}`;

    const fromDateFormatted = dateRange.from ? moment(dateRange.from, 'yyyy-MM-DDTHH:mm:ss').format('yyyy-MM-DD') : null;
    const toDateFormatted = dateRange.from ? moment(dateRange.to, 'yyyy-MM-DDTHH:mm:ss').format('yyyy-MM-DD') : null;

    const isSameDate = isStrict ? startDate === fromDateFormatted && endDate === toDateFormatted : false;

    const marginatorPrice = price / (1 - (value / 100));
    //const marginatorSavings = publicPrice - marginatorPrice;
    const marginatorCommission = marginatorPrice - price;

    const factor = Math.pow(10, 0);
    const savings = (!!marginatorPrice && !!publicPrice && publicPrice - marginatorPrice >= 0) ? publicPrice - marginatorPrice : null;
    const savingsPercent = !!savings && !!publicPrice ? Math.round(((savings / publicPrice) * 100) * factor) / factor : 0;

    const priceInt = marginatorPrice;
    const marginatorpricePerNight = totalNights ? priceInt / totalNights : priceInt;

    const maxWalletClientCashInt = maxWalletClientCash;
    const maxRewardWalletAmountInt = maxRewardWalletAmount;

    const walletPrices = getWalletPrices(account, userWalletData, selectedCondoSearchClientCash, priceInt, maxWalletClientCashInt, WalletPriceType.Details);
    const finalPriceCalculated = priceInt - (walletPrices?.clientCashAmountApplied ? walletPrices?.clientCashAmountApplied : 0);
    const finalPriceMax = priceInt - (walletPrices?.maxClientCashAmountAllow ? walletPrices?.maxClientCashAmountAllow : 0);

    const currency = getSelectedCurrency(account);

    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

    const finalPrice = displayWalletSavings ? finalPriceMax : finalPriceCalculated;
    
    const isRefundability = refundability === RefundableEnum.Refundable;

    const certificateApplied = isCertificateApplied(account as IAccount, certificateId);
    const displayTotal = certificateApplied ? price : finalPrice;

    return (
      <div className={`unit-row ${isSameDate ? 'same-dates' : ''}`}>
        {isSameDate ? (
          <>
            <div className="same-dates-information"></div>
            <div className="same-dates-information-icon">
              <Tooltip title={<FormattedMessage id="condos.your.selection" />} placement="top" >
                <FontAwesomeIcon icon={faCircleInfo} />
              </Tooltip>
            </div>
          </>) : null }
        <div className="unit-row__left-block">
          <p className={baseClassName} style={{ width: '220px' }}>
            <label>{name}</label>
            <label>
              <span className="units-search-unit__unit-details-text" style={{ fontWeight: 'normal' }} onClick={this.handleCancel}>
                <FormattedMessage id="more.info" />
              </span>
            </label>
          </p>
          <p className={baseClassNameMobile} style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '14px', fontWeight: 'bold' }}>{name}</label>
            <label>
              <span className="units-search-unit__unit-details-text" style={{ fontWeight: 'normal' }} onClick={this.handleCancel}>
                <FormattedMessage id="more.info" />
              </span>
            </label>
          </p>
          {/* Old modal kept for reference — the old list now opens the new, richer modal below.
          <ModalUnitDetails
            condo={condo?.condoDetails}
            unit={unit}
            visible={isModalVisible}
            onCancel={this.handleCancel}
            onReserve={() => this.handleReviewCondo(marginatorCommission, account?.name, condoId, unitId, walletPrices)}
            walletPrices={walletPrices}
            adultsCount={condoGuestsStore?.adultsCount}
            childrenCount={condoGuestsStore?.kidsCount}
          /> */}
          <ModalUnitDetailsNew
            condo={condo?.condoDetails}
            unit={unit}
            visible={isModalVisible}
            onCancel={this.handleCancel}
            walletPrices={walletPrices}
            adultsCount={condoGuestsStore?.adultsCount}
            childrenCount={condoGuestsStore?.kidsCount}
            savings={savings}
            savingsPercent={savingsPercent}
            onReserve={() => {
              this.handleCancel();
              this.handleReviewCondo(marginatorCommission, account?.name, condoId, unitId, walletPrices);
            }}
            certificateApplied={certificateApplied}
          />
        </div>

        <div className="unit-row__left-block">
          <p
            className={baseClassNameDateOnlyAlternative}
            style={{ minWidth: '180px' }}
          >
            <label>
              {parse(formatDateCheckInOutWithLabel(dateRange.from))} -{' '}
            </label>
            <label>
              {parse(formatDateCheckInOutWithLabel(dateRange.to))}
            </label>
          </p>
          <div className={baseClassNameOnlyAlternativeMobile}>
            <p className="unit-row__date-title">
              <FormattedMessage id="check.in" />:
            </p>
            <p className="unit-row__date-info">
              {formatDateCheckInOut(dateRange.from, DATE_FORMAT_UNIT)}
            </p>
          </div>
          <div className={baseClassNameOnlyAlternativeMobile} >
            <p className="unit-row__date-title">
              <FormattedMessage id="check.out" />:
            </p>
            <p className="unit-row__date-info">
              {formatDateCheckInOut(dateRange.to, DATE_FORMAT_UNIT)}
            </p>
          </div>
        </div>

        <div className="unit-row__left-block">
          <p
            className={baseClassNameOnlyAlternative}
            style={{ width: '90px' }}
          >
            <label>
              {totalNights}
            </label>
          </p>
          <div className={baseClassNameOnlyAlternativeMobile}>
            <p className="unit-row__date-title">
              <FormattedMessage id="nigths" />:
            </p>
            <p className="unit-row__date-info">
              <FormattedMessage
                id="nights"
                values={{ count: totalNights }}
              />
            </p>
          </div>
        </div>

        <div className="unit-row__left-block">
          <p className={baseClassNameOnlyAlternative} style={{ width: '90px' }}>
            <label>
              {bedroomsCount} / {bathroomsCount}
            </label>
          </p>
          <div className={baseClassNameOnlyAlternativeMobile} >
            <p className="unit-row__date-title">
              <FormattedMessage id="bedrooms.bathrooms" />:
            </p>
            <p className="unit-row__date-info">
                {bedroomsCount} / {bathroomsCount}
            </p>
          </div>
        </div>

        <div className="unit-row__left-block">
          <p className={baseClassNameOnlyAlternative} style={{ width: '70px' }}>
            <label>
              {maxOccupancy}
            </label>
          </p>
          <div className={baseClassNameOnlyAlternativeMobile}>
            <p className="unit-row__date-title">
              <FormattedMessage id="guests" />:
            </p>
            <p className="unit-row__date-info">
              {maxOccupancy}{'  '}
              {maxOccupancy > 0 && (
                <span className="unit-main-info__guests">
                  {creatingArrayWithNumber(maxOccupancy, 1).map((_value, i) => (
                    <span className="unit-main-info__icon-wrapper" key={i}>
                      <ManDarkSvg />
                    </span>))}
                </span>)}
            </p>
          </div>
        </div>
        
        <div className="unit-row__price-total-wallet mobile">
          <WalletMessage
            price={priceInt}
            currency={currency}
            type={WalletPriceType.Details}
            isCondo
            isOneLine
          />
          <WalletRewardMessage
            price={priceInt}
            pricePerNight={marginatorpricePerNight}
            maxRewardCash={maxRewardWalletAmountInt}
            currency={currency}
            type={WalletPriceType.Details}
          />
        </div>

        <div className="unit-row__right-block">
          <div className="unit-row__right-block-wrapper">
            <div className="unit-row__price">
              <div className="unit-row__price-total">
                {/*isWalletSearch ? (
                  <div className="unit-row__price-total-wallet desktop">
                    <WalletMessage
                      price={priceInt}
                      currency={currency}
                      type={WalletPriceType.Details}
                      isCondo
                      isShort
                      style={{ marginBottom: '10px' }}
                    />
                    <WalletRewardMessage
                      price={priceInt}
                      pricePerNight={marginatorpricePerNight}
                      maxRewardCash={maxRewardWalletAmountInt}
                      currency={currency}
                      type={WalletPriceType.Details}
                      style={{ marginBottom: '10px' }}
                      isShort={true}
                    />
                  </div>) : null*/}

                {(displayWalletSavings && walletPrices?.priceSavings > 0 && !certificateApplied) ? (
                  <div className="unit-row__for-info">
                    <p className="unit-row__for-info-text public">
                      <Currency currency={currency} />{renderNumber(Math.floor(priceInt))}
                    </p>
                  </div>) : null}

                <p className="unit-row__price-total-text"><Currency currency={currency} />{renderNumber(Math.floor(displayTotal))}</p>

                {!certificateApplied && <div className="unit-row__for-info">
                  <p className="unit-row__for-info-text">
                    <span className="unit-row__price-includes-taxes">
                      <FormattedMessage id="abbreviated.price.includes.taxes" />
                    </span>
                  </p>
                </div>}
                
                {(!!savingsPercent && savingsPercent > 0 && !displayWalletSavings && !certificateApplied) ? (
                  <div className="unit-row__for-info">
                    <p className="unit-row__for-info-text savings">
                      <FormattedMessage id={account?.forceMemberWording ? 'member.save' : 'save'} /> {savingsPercent}%
                    </p>
                  </div>) : null}

                {(displayWalletSavings && walletPrices?.priceSavings > 0 && !certificateApplied) ? (
                  <div className="unit-row__for-info">
                    <p className="unit-row__for-info-text savings">
                      <FormattedMessage id="save.up.to" values={{ number: walletPrices?.priceSavings ? walletPrices?.priceSavings : '<1' }} />
                    </p>
                  </div>) : null}
                {certificateApplied && <p className="unit-row__certificate-applied"><FormattedMessage id="certificates.certificateApplied.description" /></p>}
              </div>
            </div>
          </div>

          {!certificateApplied && (
          <div className="unit-row__right-block-wrapper refundable">
            <div className="unit-row__right-block">
              <div className="unit-row__refundable" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div
                  className={isRefundability ? 'unit-row__refundable-ref' : 'unit-row__refundable-nonref'}
                  onClick={this.showCancellationPolicies}
                >
                  <span>
                    <FormattedMessage id={isRefundability ? 'refundable' : 'non.refundable'} />
                  </span>
                  <InfoSvg />
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {!certificateApplied && (
        <div className="unit-row__right-block refundable">
          <div className="unit-row__refundable">
            <div
              className={isRefundability ? 'unit-row__refundable-ref' : 'unit-row__refundable-nonref'}
              onClick={this.showCancellationPolicies}
            >
              <span>
                <FormattedMessage id={isRefundability ? 'refundable' : 'non.refundable'} />
              </span>
              <InfoSvg />
            </div>
          </div>
        </div>
        )}

        <div className="unit-row__right-block">
          <div className="unit-row__action">
            <BlueButton onClick={() => this.handleReviewCondo(marginatorCommission, account?.name, condoId, unitId, walletPrices)}>
              <FormattedMessage id="reserve.unit" />
            </BlueButton>
          </div>
        </div>

        <ModalCancellationPolicies
          isForCondo={true}
          cancellationPoliciesText={cancellationPolicyText}
          visible={isModalCancellationPoliciesVisible}
          onCancel={this.handleCancelModalCancellationPolicies}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condosStore: state.condosStore,
    condoDetailsStore: state.condoDetailsStore,
    condoGuestsStore: state.condoGuestsStore,

    unitsSearchStore: state.unitsSearchStore,

    marginatorStore: state.marginatorStore,

    loginStore: state.loginStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setSelectedCondoSearchClientCash: condosActions.setSelectedCondoSearchClientCash,
  setSelectedCondoReviewClientCash,
  setSelectedCondoReviewClientCashStr,
  setMarginatorCommission: marginatorActions.setCommission,
};


export const CondoUnitRow = connect(mapStateToProps, mapDispatchToProps)(withRouter(CondoUnitRowComponent));
